import dayjs from 'dayjs'
import { UAParser } from 'ua-parser-js'
import { CalendarDayMode } from '../view-models/category'
import { FilterTypes, FilterOptionKey, CategorySortKey } from '@/constants/filters'

export const assetsPublicPath = () => {
  const config = useRuntimeConfig()
  const assetsPublicPath = config.public.assetsPublicPath
  return assetsPublicPath
}

export const getAssetsPath = (path = '') => {
  const config = useRuntimeConfig()
  const imageCDN = config.public.assetsPublicPath
  const isNotDevEnv =
    config.public.CONFIG_ENV === 'prod' ||
    config.public.CONFIG_ENV === 'stg' ||
    config.public.CONFIG_ENV === 'qa' ||
    config.public.CONFIG_ENV === 'beta'
  if (path.startsWith('_nuxt/img') && isNotDevEnv) {
    path = imageCDN + path.substr('_nuxt/img'.length)
  }
  return path
}

export function sleep(time = 1000) {
  return new Promise(resolve => {
    setTimeout(resolve, time)
  })
}

export const getIsCategoryPageQueryStringValid = (key, value, filters) => {
  switch (key) {
    case FilterTypes.START_TIME:
    case FilterTypes.END_TIME: {
      const dateComponent = filters.components?.find(component => component.name === FilterOptionKey.DATE)
      if (!dateComponent) {
        return false
      }
      let lastAvailableDay
      if (filters.day_mode === CalendarDayMode.SevenDays) {
        lastAvailableDay = dayjs().add(7, 'day')
      } else if (filters.day_mode === CalendarDayMode.ThirtyDays) {
        lastAvailableDay = dayjs().add(30, 'day')
      } else if (filters.day_mode === CalendarDayMode.OneHundredEightyDays) {
        lastAvailableDay = dayjs().add(180, 'day')
      }
      return lastAvailableDay && !dayjs(value).isBefore(dayjs(), 'day') && !dayjs(value).isAfter(lastAvailableDay, 'day')
    }

    case FilterTypes.LIMIT: {
      const component = filters.components.find(component => component.name === FilterOptionKey.HEAD_COUNT)
      return component !== undefined && component.options.map(e => +e.value).includes(+value)
    }
    case FilterTypes.MIN_BRANCH_RATING: {
      const component = filters.components.find(component => component.name === FilterOptionKey.RATING)
      return component !== undefined && component.options.map(e => +e.value).includes(+value)
    }
    case FilterTypes.MIN_PRICE:
    case FilterTypes.MAX_PRICE: {
      const component = filters.components.find(component => component.name === FilterOptionKey.PRICE)
      return (
        component !== undefined &&
        parseFloat(value) > parseFloat(component.options?.[0].value) &&
        parseFloat(value) < parseFloat(component.options?.[1].value)
      )
    }
    case FilterTypes.SORTBY: {
      return Object.values(CategorySortKey).includes(value)
    }
    case FilterTypes.FILTER_TIME:
    case FilterTypes.FILTER_TAGS:
    case FilterTypes.FILTER_KEYWORD:
    case FilterTypes.SOURCE_CATEGORY_ID:
      return true

    case FilterTypes.FILTER_TIMES: {
      return filters.components?.some(component => component.name === FilterOptionKey.DATE) ?? false
    }

    default:
      return false
  }
}

/**
 *
 * @param {'male' | 'female'} gender
 * @returns
 */
export function adaptGenderCode(gender) {
  switch (gender) {
    case 'male':
      return 1
    case 'female':
      return 0
    default:
      return null
  }
}

export const windowDetector = new Proxy(
  {
    parseUA: (ua = '') => {
      return new UAParser(ua)
    },
    isInAppBrowser: false,
    isInFunNowApp: false,
    isInThirdAppBrowser: false,
    isInWebBrowser: false,
  },
  {
    get: (target, prop) => {
      try {
        const rules = ['WebView', '(iPhone|iPod|iPad)(?!.*Safari/)', 'Android.*(wv)']
        const webViewRegex = new RegExp(`(${rules.join('|')})`, 'ig')
        switch (prop) {
          case 'isInAppBrowser':
            return !!window.navigator.userAgent.match(webViewRegex)
          case 'isInFunNowApp':
            return !!window.navigator.userAgent.match(/FunNow/) && !!window.navigator.userAgent.match(webViewRegex)
          case 'isInThirdAppBrowser':
            return !window.navigator.userAgent.match(/FunNow/) && !!window.navigator.userAgent.match(webViewRegex)
          case 'isInWebBrowser':
            return !window.navigator.userAgent.match(webViewRegex)
          default:
            return target[prop]
        }
      } catch (error) {
        return target[prop]
      }
    },
  }
)

/**
 * @param {number} regionId -
 * - 1 = Taipei
 * - 3 = Taichung
 * - 6 = Tainan｜Kaohsiung
 */
export function getIsTaiwanRegion(regionId) {
  return regionId === 1 || regionId === 3 || regionId === 6
}

export async function retry(action, { maxRetryCount, sleepTime, isDelayFirstTime } = { maxRetryCount: 3, sleepTime: 500, isDelayFirstTime: false }) {
  let retryCount = 0
  if (isDelayFirstTime) {
    await sleep(sleepTime)
  }
  while (retryCount < maxRetryCount) {
    try {
      return await action()
    } catch (error) {
      retryCount++
      if (retryCount === maxRetryCount) {
        throw error
      }
      await sleep(sleepTime)
    }
  }
}

export const getTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return ''
  }
}

export default {
  assetsPublicPath,
  getAssetsPath,
  sleep,
  getIsCategoryPageQueryStringValid,
  adaptGenderCode,
  windowDetector,
  getIsTaiwanRegion,
  getTimeZone,
  retry,
}
